import RichTextRenderer from '../components/RichTextRenderer';
import React, { useEffect, useState, useCallback } from 'react';
import { getProgramById, getMediaUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { ICONS } from '../constants';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { formatCategoryName } from '../utils/formatCategory';

interface ProgramDetailProps {
  slug: string;
}

const ProgramDetail: React.FC<ProgramDetailProps> = ({ slug }) => {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = (lang && ['tr', 'en', 'ar', 'krm'].includes(lang)) ? lang : locale;

  const [program, setProgram] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const [prevSlug, setPrevSlug] = useState(slug);
  const [prevLocale, setPrevLocale] = useState(currentLang);

  if (slug !== prevSlug || currentLang !== prevLocale) {
    setPrevSlug(slug);
    setPrevLocale(currentLang);
    setProgram(null);
    setLoading(true);
  }

  useEffect(() => {
    const fetchProgram = async () => {
      setLoading(true);
      const data = await getProgramById(slug, currentLang);
      setProgram(data);
      setLoading(false);
    };

    if (slug) {
      fetchProgram();
    }
  }, [slug, currentLang]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null || !program?.gallery) return;
      if (e.key === 'Escape') setSelectedImageIndex(null);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, program]);

  // Scroll to top when loading finishes to ensure the page starts at the top
  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  const nextImage = useCallback(() => {
    if (!program?.gallery) return;
    setSelectedImageIndex((prev) => (prev !== null && prev < program.gallery.length - 1 ? prev + 1 : 0));
  }, [program]);

  const prevImage = useCallback(() => {
    if (!program?.gallery) return;
    setSelectedImageIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : program.gallery.length - 1));
  }, [program]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-brand-purple-900"></div>
      </div>
    );
  }

  const emptyMessages: Record<string, { title: string; desc: string; button: string }> = {
    tr: {
      title: "Program Mevcut Değil",
      desc: "Bu program seçilen dilde henüz yayınlanmamış veya çevrilmemiştir.",
      button: "Programlara Dön"
    },
    en: {
      title: "Program Not Available",
      desc: "This program has not been published or translated in the selected language yet.",
      button: "Back to Programs"
    },
    krm: {
      title: "Bername Peyda Nebû",
      desc: "Ev bername hîn bi zimanê hilbijartî nehatiye weşandin an wergerandin.",
      button: "Vegere Bernameyan"
    },
    ar: {
      title: "البرنامج غير متوفر",
      desc: "هذا البرنامج لم يتم نشره أو ترجمته باللغة المختارة بعد.",
      button: "العودة إلى البرامج"
    }
  };

  const msg = emptyMessages[currentLang] || emptyMessages.tr;

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-neutral-bg px-4 py-20 animate-in fade-in duration-500">
        <div className="max-w-md w-full bg-white rounded-[2rem] p-8 md:p-10 shadow-xl border border-brand-purple-50/50 text-center space-y-6">
          <div className="w-16 h-16 bg-brand-purple-50 rounded-full flex items-center justify-center mx-auto text-brand-purple-600">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-serif font-bold text-brand-purple-900">{msg.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed font-light">{msg.desc}</p>
          </div>
          <div className="pt-4">
            <Link 
              to={`/${currentLang}/programs`} 
              className="inline-flex w-full items-center justify-center bg-brand-purple-900 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-brand-purple-700 hover:-translate-y-0.5 active:scale-95 transition-all"
            >
              {msg.button}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const IconComp = ICONS[program.icon as keyof typeof ICONS] || ICONS.Legal;
  const gallery = program.gallery || [];

  return (
    <>
      <article className="min-h-screen bg-brand-neutral-bg pt-20 pb-20 animate-in fade-in duration-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-16">
            <button 
              onClick={() => navigate(`/${currentLang}/programs`)}
              className="flex items-center text-brand-purple-500 font-bold text-sm uppercase tracking-widest hover:text-brand-purple-900 transition-colors mb-8 group"
            >
              <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('nav.all_programs')}
            </button>

            <div className="flex items-center space-x-4 mb-6 text-brand-purple-600">
              <div className="w-12 h-12 bg-brand-purple-100 rounded-2xl flex items-center justify-center">
                <IconComp className="w-6 h-6" />
              </div>
              <span className="font-bold tracking-widest uppercase text-xs">{formatCategoryName(program.category, t)}</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-serif font-bold text-brand-purple-900 leading-tight mb-6">
              {program.title}
            </h1>
            
            {program.description && (
              <p className="text-xl text-gray-500 font-light leading-relaxed border-l-4 border-brand-purple-200 pl-6">
                {program.description}
              </p>
            )}
          </div>

          {/* Image Gallery */}
          {gallery.length > 0 && (
            <div className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-4">
              {gallery.map((img: any, idx: number) => {
                const imgUrl = img.url ? getMediaUrl(img.url) : '';
                return (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`rounded-[2rem] overflow-hidden shadow-lg cursor-pointer relative group ${idx === 0 && gallery.length % 2 !== 0 ? 'md:col-span-2 aspect-[21/9]' : 'aspect-video'}`}
                  >
                    <img 
                      src={imgUrl} 
                      alt={`${program.title} fotoğraf ${idx + 1}`} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <svg className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Content */}
          <div className="bg-white rounded-[3rem] p-8 md:p-16 shadow-xl shadow-brand-purple-900/5">
            <div className="prose prose-lg prose-purple max-w-none text-gray-600 font-light leading-relaxed">
              <RichTextRenderer content={program.content} />
            </div>
          </div>
        </div>
      </article>

      {/* Lightbox / Modal */}
      {selectedImageIndex !== null && gallery[selectedImageIndex] && (
        <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
          
          {/* Close button */}
          <button 
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors p-2"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous button */}
          {gallery.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 md:left-10 text-white/50 hover:text-white transition-colors p-4 bg-black/20 hover:bg-black/50 rounded-full"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Current Image */}
          <div className="w-full max-w-5xl max-h-[85vh] p-4 flex flex-col items-center">
            <img 
              src={getMediaUrl(gallery[selectedImageIndex].url)}
              alt={`${program.title} fotoğraf ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            />
            <div className="text-white/70 mt-4 text-sm font-medium tracking-widest">
              {selectedImageIndex + 1} / {gallery.length}
            </div>
          </div>

          {/* Next button */}
          {gallery.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 md:right-10 text-white/50 hover:text-white transition-colors p-4 bg-black/20 hover:bg-black/50 rounded-full"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

        </div>
      )}
    </>
  );
};

export default ProgramDetail;
